import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { confirmPassword } from 'api';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import validate from './validate';
import PassInput from 'components/atoms/passInput';
import { 
  Container, 
  Paper, 
  Box, 
  Typography,
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent,
  DialogTitle,
  Avatar
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const ResetConfirm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { slug } = params;
  const [open, setOpen] = useState(false);
  const [resetStatus, setResetStatus] = useState(0);
  
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(validate),
    defaultValues: { password: '' }
  });

  const handleClose = () => {
    setOpen(false);
    if (resetStatus === 1) {
      navigate('/login');
    }
  };

  const verifyReset = async (data) => {
    const postData = { password: data.password, slug };
    try {
      await confirmPassword(postData);
      setOpen(true);
      setResetStatus(1);
    } catch {
      setOpen(true);
      setResetStatus(-1);
    }
  };
  
  const getRandomSubtitle = (subtitles) => {
    return subtitles[Math.floor(Math.random() * subtitles.length)];
  };
  
  const subtitles = [
    "Set your new password to continue",
    "Create a strong password for better security",
    "Choose a password you'll remember but others can't guess",
    "Secure your account with a new password",
    "Reset complete - just set your new password"
  ];

  const handleVerifyReset = handleSubmit((data) => {
    verifyReset(data);
  });

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 200px)',
        py: 4
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: {
            xs: 'calc(100% - 20px)', // Full width minus margins on mobile
            sm: 'calc(100% - 40px)', // Slightly different on small screens
            md: 450, // Fixed width on medium and up
            lg: 450 // Consistent on large screens
          },
          maxWidth: {
            xs: 'none', // No max-width on mobile
            md: 450 // Max width on desktop
          },
          margin: 'auto', // Center the paper
          p: {
            xs: 2, // Less padding on mobile
            md: 4 // More padding on desktop
          },
          borderRadius: 2
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Avatar sx={{ m: '0 auto', bgcolor: 'primary.main', mb: 1 }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography
            variant="h4"
            color="primary"
            sx={{ fontWeight: 700, mb: 1 }}
          >
            {t('reset.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getRandomSubtitle(subtitles)}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleVerifyReset} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please choose a strong password that is at least 8 characters long with a mix of letters, numbers, and symbols.
          </Typography>
          
          <PassInput
            label={t('login.passwordLabel')}
            name='password'
            control={control}
            error={errors.password}
            sx={{ width: '100%', mb: 2 }}
          />
          
          <Button 
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={isSubmitting}
            sx={{ 
              mt: 2, 
              mb: 2,
              py: 1.2,
              fontWeight: 500
            }}
          >
            {t('reset.update')}
          </Button>
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button 
              variant="text" 
              color="primary"
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none' }}
            >
              Back to login
            </Button>
          </Box>
        </Box>
      </Paper>
      
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
            maxWidth: 400
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {resetStatus === 1 ? (
              <CheckCircleOutlineIcon color="success" sx={{ mr: 1, fontSize: 28 }} />
            ) : (
              <ErrorOutlineIcon color="error" sx={{ mr: 1, fontSize: 28 }} />
            )}
            {resetStatus === 1 ? "Password Reset Successful" : "Password Reset Failed"}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {resetStatus === 1 ? (
            <Typography variant="body1">
              Your password has been reset successfully. You can now log in to your account using your new password.
            </Typography>
          ) : (
            <Typography variant="body1">
              We couldn&apos;t reset your password. This may be because the reset link has expired or is invalid. Please try requesting a new password reset.
            </Typography>
          )}
        </DialogContent>
        
        <DialogActions sx={{ pt: 2 }}>
          <Button 
            onClick={handleClose} 
            variant="contained" 
            color={resetStatus === 1 ? "primary" : "error"}
            autoFocus
            sx={{ textTransform: 'none' }}
          >
            {resetStatus === 1 ? "Go to Login" : "Try Again"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ResetConfirm;